import type { ComponentPropsWithRef } from 'react'
import React, { forwardRef, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KibChevronDownIcon } from '@chewy/kib-icons-react'
import classNames from 'classnames'

import styles from './RhapInlineSelect.module.scss'

export interface RhapInlineSelectOption {
  label: string
  value: string
}

export interface RhapInlineSelectProps
  extends Omit<ComponentPropsWithRef<'select'>, 'className' | 'children'> {
  className?: string
  hiddenLabel?: boolean
  label: string
  options: RhapInlineSelectOption[]
}

const RhapInlineSelect = forwardRef<HTMLSelectElement, RhapInlineSelectProps>(
  (
    { className, label, hiddenLabel = false, disabled, options, ...props },
    ref,
  ) => {
    const { t } = useTranslation('Agent')

    const selectedText = useMemo(() => {
      const selectedOption = options.find((opt) => opt.value === props.value)
      // Fallback to first option's label if current value doesn't match any option
      return selectedOption?.label ?? options[0]?.label ?? ''
    }, [props.value, options])

    const [displayText, setDisplayText] = useState(() => selectedText)
    const [isLoading, setIsLoading] = useState(false)

    // Sync display text when props update and clear loading state
    useEffect(() => {
      setDisplayText(selectedText)
      setIsLoading(false)
    }, [selectedText, props.value])

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newValue = e.target.value
      const newOption = options.find((opt) => opt.value === newValue)
      if (newOption) {
        // Optimistically update display and enter loading state
        setDisplayText(newOption.label)
        setIsLoading(true)
      }
      props.onChange?.(e)
    }

    return (
      <div className={classNames(styles.container, className)}>
        {!hiddenLabel && (
          <label className={styles.label} htmlFor={props.id}>
            {label}
          </label>
        )}
        <div className={styles.selectWrapper}>
          {/* Mirror element to display selected text with ellipsis */}
          <span
            aria-hidden
            className={classNames(styles.mirror, {
              [styles.loading]: isLoading,
            })}
          >
            {displayText}
          </span>
          <select
            {...props}
            aria-label={hiddenLabel ? label : undefined}
            className={classNames(styles.select, {
              [styles.disabled]: disabled || isLoading,
            })}
            disabled={disabled || isLoading}
            ref={ref}
            onChange={handleChange}
          >
            {options.length > 1 && (
              <option disabled className={styles.optionHeader}>
                {t('Agent:AVAILABLE_OPTIONS', { optionCount: options.length })}
              </option>
            )}
            {options.map((option) => (
              <option
                data-viewing-text={t('Agent:CURRENTLY_VIEWING')}
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </select>
          <div className={styles.iconContainer}>
            <KibChevronDownIcon
              className={styles.icon}
              height={16}
              width={16}
            />
          </div>
        </div>
      </div>
    )
  },
)

RhapInlineSelect.displayName = 'RhapInlineSelect'

export default RhapInlineSelect
